import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr, fmt, fmtNum, fileUrl, today, monthStart } from "@/lib/api";
import { Button, Input, Textarea, Label, Card, Modal } from "@/components/kit";
import { Plus, Pencil, Trash2, Clock, Truck, Package, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { project_id: "", date: today(), work_hours: 0, notes: "", machines: [], materials: [], photos: [] };

export default function WorkLog() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [machines, setMachines] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState({ project_id: "", from: monthStart(), to: today() });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);

  const loadLists = () => {
    api.get("/projects").then((r) => setProjects(r.data));
    api.get("/machines").then((r) => setMachines(r.data));
    api.get("/materials").then((r) => setMaterials(r.data));
  };
  const loadEntries = () => {
    const params = { date_from: filter.from, date_to: filter.to };
    if (filter.project_id) params.project_id = filter.project_id;
    api.get("/entries", { params }).then((r) => setEntries(r.data)).catch((e) => toast.error(apiErr(e)));
  };
  useEffect(() => { loadLists(); }, []);
  useEffect(() => { loadEntries(); }, [filter]); // eslint-disable-line

  const openNew = () => {
    setForm({ ...emptyForm, project_id: projects[0]?.id || "" });
    setEditId(null); setOpen(true);
  };
  const openEdit = (e) => {
    setForm({ project_id: e.project_id, date: e.date, work_hours: e.work_hours, notes: e.notes || "",
      machines: e.machines || [], materials: e.materials || [], photos: e.photos || [] });
    setEditId(e.id); setOpen(true);
  };

  const addMachine = () => {
    const m = machines[0];
    setForm((f) => ({ ...f, machines: [...f.machines, { machine_id: m?.id || "", name: m?.name || "", hours: 0, rate: m?.hourly_rate || 0 }] }));
  };
  const setMachine = (i, mid) => {
    const m = machines.find((x) => x.id === mid);
    setForm((f) => { const arr = [...f.machines]; arr[i] = { ...arr[i], machine_id: mid, name: m?.name || "", rate: m?.hourly_rate || 0 }; return { ...f, machines: arr }; });
  };
  const addMaterial = () => {
    const m = materials[0];
    setForm((f) => ({ ...f, materials: [...f.materials, { material_id: m?.id || "", name: m?.name || "", quantity: 0, unit: m?.unit || "st", unit_cost: m?.unit_cost || 0 }] }));
  };
  const setMaterial = (i, mid) => {
    const m = materials.find((x) => x.id === mid);
    setForm((f) => { const arr = [...f.materials]; arr[i] = { ...arr[i], material_id: mid, name: m?.name || "", unit: m?.unit || "st", unit_cost: m?.unit_cost || 0 }; return { ...f, materials: arr }; });
  };

  const upload = async (files) => {
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, photos: [...f.photos, data.path] }));
      }
    } catch (e) { toast.error(apiErr(e)); } finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.project_id) return toast.error("Välj ett projekt");
    const payload = {
      ...form,
      work_hours: Number(form.work_hours),
      machines: form.machines.map((m) => ({ ...m, hours: Number(m.hours), rate: Number(m.rate) })),
      materials: form.materials.map((m) => ({ ...m, quantity: Number(m.quantity), unit_cost: Number(m.unit_cost) })),
    };
    try {
      if (editId) await api.put(`/entries/${editId}`, payload);
      else await api.post("/entries", payload);
      toast.success("Rapport sparad");
      setOpen(false); loadEntries();
    } catch (err) { toast.error(apiErr(err)); }
  };
  const del = async (id) => { if (!window.confirm("Ta bort rapporten?")) return; try { await api.delete(`/entries/${id}`); loadEntries(); } catch (e) { toast.error(apiErr(e)); } };

  const totals = entries.reduce((a, e) => a + (e.cost?.total || 0), 0);

  return (
    <div className="rise">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-amber-500">Fältrapportering</p>
          <h1 className="font-display text-3xl font-extrabold text-white">Tidrapport</h1>
        </div>
        <Button data-testid="add-entry-button" onClick={openNew}><Plus size={16} /> Ny rapport</Button>
      </div>

      <Card className="mb-5 p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div>
            <Label>Projekt</Label>
            <select data-testid="filter-project" className="w-full rounded-lg bg-slate-950/60 border border-slate-700 px-3 py-2 text-sm text-slate-100" value={filter.project_id} onChange={(e) => setFilter({ ...filter, project_id: e.target.value })}>
              <option value="">Alla projekt</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div><Label>Från</Label><Input data-testid="filter-from" type="date" value={filter.from} onChange={(e) => setFilter({ ...filter, from: e.target.value })} /></div>
          <div><Label>Till</Label><Input data-testid="filter-to" type="date" value={filter.to} onChange={(e) => setFilter({ ...filter, to: e.target.value })} /></div>
          {user.role === "owner" && (
            <div className="flex items-end">
              <div className="w-full rounded-lg bg-amber-500/10 px-4 py-2.5 text-right">
                <div className="text-[10px] uppercase tracking-wider text-amber-400/70">Summa period</div>
                <div className="font-display text-lg font-bold text-amber-400">{fmt(totals)}</div>
              </div>
            </div>
          )}
        </div>
      </Card>

      <div className="space-y-3" data-testid="entries-list">
        {entries.map((e) => {
          const p = projects.find((x) => x.id === e.project_id);
          const canEdit = user.role === "owner" || e.employee_id === user.id;
          return (
            <Card key={e.id} className="p-4" data-testid={`entry-${e.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-amber-400">{e.date}</span>
                    <span className="text-slate-600">·</span>
                    <span className="text-sm font-semibold text-white">{p?.name || "—"}</span>
                  </div>
                  <div className="mt-1 text-xs text-slate-500">{e.employee_name}</div>
                  <div className="mt-3 flex flex-wrap gap-4 text-sm">
                    <span className="inline-flex items-center gap-1.5 text-slate-300"><Clock size={14} className="text-amber-400" /> {fmtNum(e.work_hours)} h</span>
                    {e.machines?.length > 0 && <span className="inline-flex items-center gap-1.5 text-slate-300"><Truck size={14} className="text-sky-400" /> {e.machines.length} maskin{e.machines.length > 1 ? "er" : ""}</span>}
                    {e.materials?.length > 0 && <span className="inline-flex items-center gap-1.5 text-slate-300"><Package size={14} className="text-violet-400" /> {e.materials.length} material</span>}
                    {e.photos?.length > 0 && <span className="inline-flex items-center gap-1.5 text-slate-300"><ImageIcon size={14} className="text-emerald-400" /> {e.photos.length} foto</span>}
                  </div>
                  {e.notes && <p className="mt-2 text-sm text-slate-400">{e.notes}</p>}
                  {e.photos?.length > 0 && (
                    <div className="mt-3 flex gap-2">
                      {e.photos.map((ph, i) => (
                        <a key={i} href={fileUrl(ph)} target="_blank" rel="noreferrer">
                          <img src={fileUrl(ph)} alt="" className="h-14 w-14 rounded-lg border border-slate-700 object-cover" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  {user.role === "owner" && (
                    <>
                      <div className="font-display text-xl font-bold text-white">{fmt(e.cost?.total)}</div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">exkl. moms</div>
                    </>
                  )}
                  {canEdit && (
                    <div className="mt-2 flex justify-end gap-1.5">
                      <Button variant="ghost" className="px-2 py-1.5" data-testid={`edit-entry-${e.id}`} onClick={() => openEdit(e)}><Pencil size={13} /></Button>
                      <Button variant="ghost" className="px-2 py-1.5 text-red-400" data-testid={`delete-entry-${e.id}`} onClick={() => del(e.id)}><Trash2 size={13} /></Button>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
        {entries.length === 0 && (
          <Card className="p-12 text-center text-slate-500">Inga rapporter i vald period. Klicka "Ny rapport" för att börja.</Card>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Redigera rapport" : "Ny tidrapport"} wide>
        <form onSubmit={save} className="space-y-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <Label>Projekt</Label>
              <select data-testid="entry-project-select" className="w-full rounded-lg bg-slate-950/60 border border-slate-700 px-3 py-2.5 text-sm text-slate-100" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} required>
                <option value="">Välj…</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div><Label>Datum</Label><Input data-testid="entry-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required /></div>
            <div><Label>Arbetstimmar</Label><Input data-testid="entry-hours-input" type="number" step="0.25" value={form.work_hours} onChange={(e) => setForm({ ...form, work_hours: e.target.value })} /></div>
          </div>

          {/* machines */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label className="mb-0">Maskintid</Label>
              <button type="button" data-testid="add-machine-line" onClick={addMachine} className="text-xs font-semibold text-amber-400 hover:text-amber-300">+ Lägg till maskin</button>
            </div>
            {form.machines.map((m, i) => (
              <div key={i} className="mb-2 flex flex-wrap items-center gap-2">
                <select className="min-w-[140px] flex-1 rounded-lg bg-slate-950/60 border border-slate-700 px-2 py-2 text-sm text-slate-100" value={m.machine_id} onChange={(e) => setMachine(i, e.target.value)}>
                  {machines.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
                <Input type="number" step="0.25" placeholder="tim" className="w-24" value={m.hours} onChange={(e) => setForm((f) => { const arr = [...f.machines]; arr[i] = { ...arr[i], hours: e.target.value }; return { ...f, machines: arr }; })} />
                <button type="button" onClick={() => setForm((f) => ({ ...f, machines: f.machines.filter((_, j) => j !== i) }))} className="text-slate-500 hover:text-red-400"><X size={16} /></button>
              </div>
            ))}
          </div>

          {/* materials */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label className="mb-0">Material</Label>
              <button type="button" data-testid="add-material-line" onClick={addMaterial} className="text-xs font-semibold text-amber-400 hover:text-amber-300">+ Lägg till material</button>
            </div>
            {form.materials.map((m, i) => (
              <div key={i} className="mb-2 flex flex-wrap items-center gap-2">
                <select className="min-w-[140px] flex-1 rounded-lg bg-slate-950/60 border border-slate-700 px-2 py-2 text-sm text-slate-100" value={m.material_id} onChange={(e) => setMaterial(i, e.target.value)}>
                  {materials.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
                <Input type="number" step="0.01" placeholder="antal" className="w-24" value={m.quantity} onChange={(e) => setForm((f) => { const arr = [...f.materials]; arr[i] = { ...arr[i], quantity: e.target.value }; return { ...f, materials: arr }; })} />
                <span className="w-10 text-xs text-slate-500">{m.unit}</span>
                <button type="button" onClick={() => setForm((f) => ({ ...f, materials: f.materials.filter((_, j) => j !== i) }))} className="text-slate-500 hover:text-red-400"><X size={16} /></button>
              </div>
            ))}
          </div>

          <div><Label>Anteckningar</Label><Textarea data-testid="entry-notes-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>

          <div>
            <Label>Foton</Label>
            <div className="flex flex-wrap items-center gap-2">
              {form.photos.map((ph, i) => (
                <div key={i} className="relative">
                  <img src={fileUrl(ph)} alt="" className="h-16 w-16 rounded-lg border border-slate-700 object-cover" />
                  <button type="button" onClick={() => setForm((f) => ({ ...f, photos: f.photos.filter((_, j) => j !== i) }))} className="absolute -right-1.5 -top-1.5 rounded-full bg-red-500 p-0.5 text-white"><X size={12} /></button>
                </div>
              ))}
              <label data-testid="photo-upload-label" className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-slate-700 text-slate-500 hover:border-amber-500 hover:text-amber-400">
                <ImageIcon size={20} />
                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(Array.from(e.target.files))} />
              </label>
              {uploading && <span className="text-xs text-slate-500">Laddar upp…</span>}
            </div>
          </div>

          <div className="flex items-center justify-end rounded-lg bg-slate-950/60 px-4 py-3">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Sammanfattning</div>
              <div className="text-sm text-slate-300">
                {fmtNum(form.work_hours)} arbetstim · {form.machines.length} maskin · {form.materials.length} material
              </div>
            </div>
          </div>

          <Button data-testid="save-entry-button" type="submit" className="w-full">Spara rapport</Button>
        </form>
      </Modal>
    </div>
  );
}
